import { Controller } from "@hotwired/stimulus"
import { select, pointer } from "d3-selection"
import { scaleTime, scaleLinear } from "d3-scale"
import { line as d3Line, area as d3Area, curveMonotoneX } from "d3-shape"
import { axisBottom, axisLeft } from "d3-axis"
import { bisector } from "d3-array"
import { isDarkMode } from "../lib/helpers"
import { SERIES, referenceStroke } from "../lib/chart_series"
import { createReveal, reveal } from "../lib/chart_reveal"

const MARGIN = { top: 8, right: 12, bottom: 24, left: 36 }

// Module-level so chart-reload frame replacement (new controller instance) can
// restore hover / keyboard scrub the same way mouse hover already did.
let lastPointer = null
let lastScrubT = null
let scrubFocused = false

// Keyed by the frame: chart-reload replaces its children every refresh, but a page visit brings a new frame.
const reveals = new WeakMap()

export default class extends Controller {
  static values = {
    points: Array,
    range: String,
    windowSeconds: Number,
    metricLabel: String,
    instanceLabel: String,
    metricReferences: Array,
  }

  static targets = ["metricCanvas", "instanceCanvas", "liveSummary"]

  connect() {
    this.#reveal = this.#revealFor()
    this.#render()
    document.addEventListener("theme:changed", this.#onThemeChange)
    this.#observeResize()
    this.element.addEventListener("keydown", this.#onKeydown)
    this.element.addEventListener("focus", this.#onFocus)
    this.element.addEventListener("blur", this.#onBlur)
  }

  disconnect() {
    if (document.activeElement === this.element) scrubFocused = true
    this.#resizeObserver?.disconnect()
    document.removeEventListener("theme:changed", this.#onThemeChange)
    this.element.removeEventListener("keydown", this.#onKeydown)
    this.element.removeEventListener("focus", this.#onFocus)
    this.element.removeEventListener("blur", this.#onBlur)
  }

  #onThemeChange = () => this.#render()
  #resizeObserver
  #renderedWidth
  #reveal = null
  #charts = []
  #scrubIndex = null

  #revealFor() {
    const key = this.element.closest("turbo-frame") ?? this.element
    let entry = reveals.get(key)
    if (entry?.range !== this.rangeValue) {
      entry = { range: this.rangeValue, reveal: createReveal() }
      reveals.set(key, entry)
    }
    return entry.reveal
  }

  #render() {
    this.#charts = []

    const now = Date.now()

    // Tooltip value tones match Current State (astral/azure 700 light, 300 dark).
    const dark = isDarkMode()
    const metricValueColor = dark
      ? "var(--color-astral-300)"
      : "var(--color-astral-700)"
    const dynoValueColor = dark
      ? "var(--color-azure-300)"
      : "var(--color-azure-700)"

    this.#buildChart(this.metricCanvasTarget, {
      label: this.metricLabelValue,
      color: SERIES.astral.stroke,
      band: SERIES.astral.band,
      valueColor: metricValueColor,
      fields: { avg: "avg_metric", min: "min_metric", max: "max_metric" },
      references: this.metricReferencesValue,
      now,
    })

    this.#buildChart(this.instanceCanvasTarget, {
      label: this.instanceLabelValue,
      color: SERIES.azure.stroke,
      band: SERIES.azure.band,
      valueColor: dynoValueColor,
      fields: {
        avg: "avg_instance_quantity",
        min: "min_instance_quantity",
        max: "max_instance_quantity",
      },
      references: [],
      now,
    })

    this.#renderedWidth = this.metricCanvasTarget.getBoundingClientRect().width
    this.#restoreInteraction()
  }

  #restoreInteraction() {
    if (lastScrubT != null && this.pointsValue.length) {
      const idx = this.#indexNearT(lastScrubT)
      if (idx != null) {
        this.#rememberScrub(idx)
        this.#showAt(idx, {announce: scrubFocused})
        if (scrubFocused) {
          requestAnimationFrame(() => {
            if (!scrubFocused || !this.element.isConnected) return
            this.element.focus({preventScroll: true})
          })
        }
        return
      }
    }

    if (!lastPointer) return

    for (const chart of this.#charts) {
      if (chart.contains(lastPointer.x, lastPointer.y)) {
        const idx = chart.indexAt(lastPointer.x)
        this.#rememberScrub(idx)
        this.#showAt(idx, {announce: false})
        return
      }
    }
  }

  #indexNearT(t) {
    const points = this.pointsValue
    if (!points.length) return null

    // points[].t is an ISO8601 string from the server; arithmetic on those is
    // NaN and would pin every restore to index 0 (start of the chart).
    const target = this.#tMs(t)
    if (target == null) return null

    let best = 0
    let bestDist = Math.abs(this.#tMs(points[0].t) - target)
    for (let i = 1; i < points.length; i++) {
      const dist = Math.abs(this.#tMs(points[i].t) - target)
      if (dist < bestDist) {
        best = i
        bestDist = dist
      }
    }
    return best
  }

  #tMs(t) {
    if (t == null) return null
    if (typeof t === "number" && Number.isFinite(t)) return t
    const ms = new Date(t).getTime()
    return Number.isFinite(ms) ? ms : null
  }

  #rememberScrub(idx) {
    this.#scrubIndex = idx
    const point = this.pointsValue[idx]
    if (point) lastScrubT = this.#tMs(point.t)
  }

  #onKeydown = (event) => {
    const len = this.pointsValue.length
    if (!len) return

    const max = len - 1
    let idx = this.#scrubIndex ?? max

    switch (event.key) {
      case "ArrowLeft":
      case "ArrowDown":
        event.preventDefault()
        idx = Math.max(0, idx - 1)
        break
      case "ArrowRight":
      case "ArrowUp":
        event.preventDefault()
        idx = Math.min(max, idx + 1)
        break
      case "Home":
        event.preventDefault()
        idx = 0
        break
      case "End":
        event.preventDefault()
        idx = max
        break
      default:
        return
    }

    scrubFocused = true
    this.#rememberScrub(idx)
    this.#showAt(idx, {announce: true})
  }

  #onFocus = () => {
    scrubFocused = true
    if (!this.pointsValue.length) return
    if (this.#scrubIndex == null) {
      const idx =
        lastScrubT != null
          ? (this.#indexNearT(lastScrubT) ?? this.pointsValue.length - 1)
          : this.pointsValue.length - 1
      this.#rememberScrub(idx)
    }
    this.#showAt(this.#scrubIndex, {announce: true})
  }

  #onBlur = () => {
    // Defer: chart-reload removes this node mid-blur. A sync check can still
    // see isConnected, so wait a frame and only clear if we truly left focus.
    requestAnimationFrame(() => {
      if (!this.element.isConnected) return
      if (document.activeElement === this.element) return

      scrubFocused = false
      if (lastPointer) return
      lastScrubT = null
      this.#scrubIndex = null
      for (const chart of this.#charts) chart.hide()
      this.#clearLiveSummary()
    })
  }

  #showAt(idx, {announce}) {
    for (const chart of this.#charts) chart.showAt(idx)
    if (announce) this.#announce(idx)
  }

  #announce(idx) {
    if (!this.hasLiveSummaryTarget) return
    const d = this.pointsValue[idx]
    if (!d) return

    const time = this.#formatTooltipLabel(new Date(d.t))
    const metric = this.#formatSeriesSummary(
      this.metricLabelValue,
      d.avg_metric,
      d.min_metric,
      d.max_metric
    )
    const instance = this.#formatSeriesSummary(
      this.instanceLabelValue,
      d.avg_instance_quantity,
      d.min_instance_quantity,
      d.max_instance_quantity
    )
    this.liveSummaryTarget.textContent = `${time}. ${metric} ${instance}`
  }

  #clearLiveSummary() {
    if (this.hasLiveSummaryTarget) this.liveSummaryTarget.textContent = ""
  }

  #formatSeriesSummary(label, avg, min, max) {
    if (avg == null) return `${label}: no data.`
    const parts = [`${label}: average ${avg}`]
    if (min != null) parts.push(`minimum ${min}`)
    if (max != null) parts.push(`maximum ${max}`)
    return `${parts.join(", ")}.`
  }

  // Hover tooltip body: series name + Average / Minimum / Maximum rows.
  // Values use the series color (astral metric, azure dynos). Labels stay muted.
  #buildTooltipBody(label, avg, min, max, valueColor, dark) {
    const wrap = document.createElement("div")

    const seriesEl = document.createElement("div")
    seriesEl.style.cssText = `
      margin-bottom: 6px;
      font-size: 11px;
      font-weight: 600;
      color: ${valueColor};
    `
    seriesEl.textContent = label
    wrap.appendChild(seriesEl)

    if (avg == null) {
      const empty = document.createElement("div")
      empty.textContent = "No data"
      empty.style.color = dark ? "rgba(156, 163, 175, 1)" : "rgba(75, 85, 99, 1)"
      wrap.appendChild(empty)
      return wrap
    }

    const labelColor = dark ? "rgba(156, 163, 175, 1)" : "rgba(75, 85, 99, 1)"
    const rows = [
      ["Average", avg],
      ["Minimum", min],
      ["Maximum", max],
    ]

    for (const [name, value] of rows) {
      if (value == null) continue
      const row = document.createElement("div")
      row.style.cssText = `
        display: flex;
        justify-content: space-between;
        gap: 16px;
        font-size: 12px;
        line-height: 1.55;
      `
      const nameEl = document.createElement("span")
      nameEl.textContent = name
      nameEl.style.color = labelColor
      const valueEl = document.createElement("span")
      valueEl.textContent = String(value)
      valueEl.style.cssText = `font-weight: 600; font-variant-numeric: tabular-nums; color: ${valueColor};`
      row.append(nameEl, valueEl)
      wrap.appendChild(row)
    }

    return wrap
  }

  #buildChart(container, { label, color, band, valueColor, fields, references, now }) {
    container.innerHTML = ""
    container.style.position = "relative"

    const data = this.pointsValue.map((d) => ({ ...d, date: new Date(d.t) }))
    if (!data.length) return

    const dark = isDarkMode()
    const { width, height } = container.getBoundingClientRect()
    if (!width || !height) return

    const innerW = width - MARGIN.left - MARGIN.right
    const innerH = height - MARGIN.top - MARGIN.bottom

    const xScale = scaleTime()
      .domain([new Date(now - this.windowSecondsValue * 1000), new Date(now)])
      .range([0, innerW])

    const allValues = data
      .flatMap((d) => [d[fields.max], d[fields.avg], d[fields.min]])
      .filter((v) => v != null)
      .concat(references)

    const yMax = allValues.length ? Math.max(...allValues) || 1 : 1

    const yScale = scaleLinear().domain([0, yMax]).range([innerH, 0]).nice()

    const svg = select(container)
      .append("svg")
      .attr("width", width)
      .attr("height", height)

    const g = svg
      .append("g")
      .attr("transform", `translate(${MARGIN.left},${MARGIN.top})`)

    g.append("g")
      .call(axisLeft(yScale).tickSize(-innerW).tickFormat(""))
      .call((el) => el.select(".domain").remove())
      .call((el) =>
        el
          .selectAll(".tick line")
          .attr(
            "stroke",
            dark ? "rgba(55, 65, 81, 0.5)" : "rgba(229, 231, 235, 1)"
          )
      )

    const [yMin, yDomainMax] = yScale.domain()
    for (const v of references.filter((r) => r >= yMin && r <= yDomainMax)) {
      g.append("line")
        .attr("x1", 0)
        .attr("x2", innerW)
        .attr("y1", yScale(v))
        .attr("y2", yScale(v))
        .attr("stroke", referenceStroke(dark))
        .attr("stroke-width", 1)
        .attr("stroke-dasharray", "3 3")
    }

    const bandShape = d3Area()
      .defined((d) => d[fields.min] != null && d[fields.max] != null)
      .x((d) => xScale(d.date))
      .y0((d) => yScale(d[fields.min]))
      .y1((d) => yScale(d[fields.max]))
      .curve(curveMonotoneX)

    const series = g.append("g")

    series.append("path").datum(data).attr("fill", band).attr("d", bandShape)

    const lineShape = d3Line()
      .defined((d) => d[fields.avg] != null)
      .x((d) => xScale(d.date))
      .y((d) => yScale(d[fields.avg]))
      .curve(curveMonotoneX)

    series
      .append("path")
      .datum(data)
      .attr("fill", "none")
      .attr("stroke", color)
      .attr("stroke-width", 1.5)
      .attr("d", lineShape)

    reveal(series.node(), this.#reveal)

    g.append("g")
      .attr("transform", `translate(0,${innerH})`)
      .call(
        axisBottom(xScale)
          .ticks(this.#xTickCount(innerW))
          .tickFormat((d) => this.#formatLabel(d))
      )
      .call((el) => el.select(".domain").remove())
      .call((el) => el.selectAll(".tick line").remove())
      .call((el) =>
        el
          .selectAll("text")
          .attr("fill", "rgba(156, 163, 175, 1)")
          .attr("font-size", "11px")
      )
      .call((el) => this.#hideOverlappingXTicks(el))

    g.append("g")
      .call(axisLeft(yScale).ticks(4))
      .call((el) => el.select(".domain").remove())
      .call((el) => el.selectAll(".tick line").remove())
      .call((el) =>
        el
          .selectAll("text")
          .attr("fill", "rgba(156, 163, 175, 1)")
          .attr("font-size", "11px")
      )

    const crosshair = g
      .append("line")
      .attr("y1", 0)
      .attr("y2", innerH)
      .attr(
        "stroke",
        dark ? "rgba(156, 163, 175, 0.3)" : "rgba(156, 163, 175, 0.5)"
      )
      .attr("stroke-width", 1)
      .style("display", "none")

    const tooltip = document.createElement("div")
    tooltip.style.cssText = `
      position: absolute;
      pointer-events: none;
      display: none;
      min-width: 10rem;
      padding: 8px 10px;
      border-radius: 6px;
      font-size: 12px;
      line-height: 1.5;
      z-index: 10;
      background: ${dark ? "rgba(31, 41, 55, 1)" : "rgba(255, 255, 255, 1)"};
      border: 1px solid ${dark ? "rgba(55, 65, 81, 1)" : "rgba(209, 213, 219, 1)"};
      color: ${dark ? "rgba(243, 244, 246, 1)" : "rgba(31, 41, 55, 1)"};
      box-shadow: 0 4px 12px rgba(0, 0, 0, ${dark ? "0.35" : "0.10"});
    `
    container.appendChild(tooltip)

    const bisectDate = bisector((d) => d.date).center

    const showAt = (idx) => {
      const d = data[idx]
      if (!d) return

      const xPos = xScale(d.date)
      crosshair.style("display", null).attr("x1", xPos).attr("x2", xPos)

      const avg = d[fields.avg]
      const minV = d[fields.min]
      const maxV = d[fields.max]
      const timeStr = this.#formatTooltipLabel(d.date)

      const titleEl = document.createElement("div")
      titleEl.style.cssText = "font-weight: 600; margin-bottom: 4px; white-space: nowrap;"
      titleEl.textContent = timeStr

      const bodyEl = this.#buildTooltipBody(label, avg, minV, maxV, valueColor, dark)
      tooltip.replaceChildren(titleEl, bodyEl)

      // Measure after paint so multi-line width is real (not a guess).
      tooltip.style.visibility = "hidden"
      tooltip.style.display = "block"
      const tipWidth = tooltip.offsetWidth || 180
      let left =
        MARGIN.left + xPos + tipWidth + 8 > width
          ? MARGIN.left + xPos - tipWidth - 8
          : MARGIN.left + xPos + 8
      left = Math.max(0, left)

      tooltip.style.left = `${left}px`
      tooltip.style.top = `${MARGIN.top}px`
      tooltip.style.visibility = "visible"
    }

    const hide = () => {
      crosshair.style("display", "none")
      tooltip.style.display = "none"
    }

    const overlay = g
      .append("rect")
      .attr("width", innerW)
      .attr("height", innerH)
      .attr("fill", "none")
      .attr("pointer-events", "all")
      .on("mousemove", (event) => {
        lastPointer = { x: event.clientX, y: event.clientY }
        const [mx] = pointer(event)
        const idx = bisectDate(data, xScale.invert(mx))
        this.#rememberScrub(idx)
        for (const chart of this.#charts) chart.showAt(idx)
      })
      .on("mouseleave", () => {
        lastPointer = null
        if (
          (document.activeElement === this.element || scrubFocused) &&
          this.#scrubIndex != null
        ) {
          this.#showAt(this.#scrubIndex, {announce: false})
          return
        }
        for (const chart of this.#charts) chart.hide()
      })

    const overlayNode = overlay.node()

    const contains = (clientX, clientY) => {
      const rect = overlayNode.getBoundingClientRect()
      return (
        clientX >= rect.left &&
        clientX <= rect.right &&
        clientY >= rect.top &&
        clientY <= rect.bottom
      )
    }

    const indexAt = (clientX) => {
      const rect = overlayNode.getBoundingClientRect()
      return bisectDate(data, xScale.invert(clientX - rect.left))
    }

    this.#charts.push({ showAt, hide, contains, indexAt })
  }

  #observeResize() {
    this.#resizeObserver = new ResizeObserver(([entry]) => {
      if (entry.contentRect.width === this.#renderedWidth) return
      this.#render()
    })
    this.#resizeObserver.observe(this.metricCanvasTarget)
  }

  #xTickCount(innerW) {
    const labelPx = ["7d", "30d", "90d"].includes(this.rangeValue)
      ? 56
      : this.rangeValue === "1y"
        ? 64
        : 48
    const byWidth = Math.max(2, Math.floor(innerW / labelPx))
    const cap = this.rangeValue === "7d" ? 7 : 12
    return Math.min(cap, byWidth)
  }

  #hideOverlappingXTicks(axisG) {
    let lastRight = -Infinity
    axisG.selectAll(".tick").each(function () {
      const box = this.querySelector("text")?.getBoundingClientRect()
      if (!box) return
      if (box.left < lastRight + 8) {
        this.style.display = "none"
      } else {
        lastRight = box.right
      }
    })
  }

  // Compact x-axis ticks only. Hover tooltips use #formatTooltipLabel so each
  // range shows units that match its bucket (minute / hour / day).
  #formatLabel(date) {
    const {hh, mm, mon, dd, yyyy} = this.#dateParts(date)

    switch (this.rangeValue) {
      case "2h":
      case "24h":
        return `${hh}:${mm}`
      case "7d":
      case "30d":
      case "90d":
        return `${mon} ${dd}`
      case "1y":
        return `${mon} ${yyyy}`
      default:
        return `${hh}:${mm}`
    }
  }

  // Full bucket timestamp for the tooltip, keyed to chart range / bucket size:
  // 2h (1 min) and 24h (10 min) → day + HH:MM
  // 7d (1 h), 30d (6 h), 90d (12 h) → day + HH:MM
  // 1y (1 day) → full calendar day with year
  #formatTooltipLabel(date) {
    const {hh, mm, mon, dd, yyyy} = this.#dateParts(date)

    switch (this.rangeValue) {
      case "1y":
        return `${mon} ${dd}, ${yyyy}`
      case "2h":
      case "24h":
      case "7d":
      case "30d":
      case "90d":
      default:
        return `${mon} ${dd}, ${hh}:${mm}`
    }
  }

  #dateParts(date) {
    const d = date instanceof Date ? date : new Date(date)
    const pad = (n) => String(n).padStart(2, "0")
    return {
      hh: pad(d.getHours()),
      mm: pad(d.getMinutes()),
      mon: d.toLocaleString(undefined, {month: "short"}),
      dd: pad(d.getDate()),
      yyyy: d.getFullYear(),
    }
  }
}
